import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FunctionComponent,
  type SVGProps,
} from 'react'
import type {
  RichTextEditorFontSize,
  RichTextEditorToolbarItemId,
  RichTextEditorVariable,
} from './RichTextEditor.types'
import { ReactComponent as AlignLeftIcon } from '@assets/images/Icons/Align_left.svg'
import { ReactComponent as AlignCenterIcon } from '@assets/images/Icons/Align_center.svg'
import { ReactComponent as AlignRightIcon } from '@assets/images/Icons/Align_right.svg'
import { ReactComponent as AlignJustifyIcon } from '@assets/images/Icons/Align_justify.svg'
import { sanitizeRichTextHtml } from '@utils/sanitize-html.util'

export const RICH_TEXT_EDITOR_VARIABLE_CLASS = 'ds-rich-text-editor__variable'

export type TextAlignment = 'left' | 'center' | 'right' | 'justify'

const FONT_SIZE_COMMAND_MAP: Record<RichTextEditorFontSize, string> = {
  '10 pt': '1',
  '12 pt': '2',
  '14 pt': '3',
  '16 pt': '4',
  '18 pt': '5',
}

const ALIGNMENT_COMMAND_MAP: Record<TextAlignment, string> = {
  left: 'justifyLeft',
  center: 'justifyCenter',
  right: 'justifyRight',
  justify: 'justifyFull',
}

const FORMATTING_COMMANDS: RichTextEditorToolbarItemId[] = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'bulletList',
  'orderedList',
  'blockquote',
]

const EXEC_COMMAND_MAP: Partial<Record<RichTextEditorToolbarItemId, string>> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikeThrough',
  bulletList: 'insertUnorderedList',
  orderedList: 'insertOrderedList',
  blockquote: 'formatBlock',
  indentLeft: 'outdent',
  indentRight: 'indent',
  undo: 'undo',
  redo: 'redo',
}

type UseRichTextEditorOptions = {
  value?: string
  defaultValue?: string
  disabled?: boolean
  onChange?: (value: string, plainText: string) => void
}

type UseRichTextEditorResult = {
  editorRef: React.RefObject<HTMLDivElement>
  resolvedValue: string
  activeCommands: Set<RichTextEditorToolbarItemId>
  alignment: TextAlignment
  setAlignment: (alignment: TextAlignment) => void
  applyAlignment: (alignment: TextAlignment) => void
  applyFontSize: (fontSize: RichTextEditorFontSize) => void
  applyTextColor: (color: string) => void
  applyBackgroundColor: (color: string) => void
  insertText: (text: string) => void
  insertVariable: (variable: RichTextEditorVariable) => void
  runToolbarAction: (actionId: RichTextEditorToolbarItemId) => boolean
  handleInput: () => void
  handlePaste: (event: ClipboardEvent<HTMLDivElement>) => void
  refreshActiveCommands: () => void
}

const getEditorHtml = (editor: HTMLDivElement | null): string =>
  sanitizeRichTextHtml(editor?.innerHTML ?? '')

const PLAIN_TEXT_BLOCK_SELECTOR =
  'p, div, li, blockquote, h1, h2, h3, h4, h5, h6'

/**
 * Converts editor HTML to plain text, keeping line breaks for `<br>` and block
 * elements. Parsed with DOMParser so nothing (images, scripts) is ever loaded.
 */
export const richTextEditorHtmlToPlainText = (html: string): string => {
  if (!html) {
    return ''
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  doc.body
    .querySelectorAll(PLAIN_TEXT_BLOCK_SELECTOR)
    .forEach((block) => block.append('\n'))

  return (doc.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** True when HTML contains visible text (ignores empty tags / nbsp). */
export const richTextEditorHasContent = (html: string): boolean =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .trim().length > 0

const isCommandActive = (actionId: RichTextEditorToolbarItemId): boolean => {
  try {
    switch (actionId) {
      case 'bold':
        return document.queryCommandState('bold')
      case 'italic':
        return document.queryCommandState('italic')
      case 'underline':
        return document.queryCommandState('underline')
      case 'strikethrough':
        return document.queryCommandState('strikeThrough')
      case 'bulletList':
        return document.queryCommandState('insertUnorderedList')
      case 'orderedList':
        return document.queryCommandState('insertOrderedList')
      case 'blockquote': {
        const block = document.queryCommandValue('formatBlock')
        return block.toLowerCase() === 'blockquote'
      }
      default:
        return false
    }
  } catch {
    return false
  }
}

const detectAlignment = (): TextAlignment => {
  if (document.queryCommandState('justifyFull')) {
    return 'justify'
  }

  if (document.queryCommandState('justifyCenter')) {
    return 'center'
  }

  if (document.queryCommandState('justifyRight')) {
    return 'right'
  }

  return 'left'
}

export const useRichTextEditor = ({
  value,
  defaultValue = '',
  disabled = false,
  onChange,
}: UseRichTextEditorOptions): UseRichTextEditorResult => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [activeCommands, setActiveCommands] = useState<
    Set<RichTextEditorToolbarItemId>
  >(new Set())
  const [alignment, setAlignment] = useState<TextAlignment>('left')
  const resolvedValue = value ?? internalValue
  const isControlled = value !== undefined

  const syncValue = useCallback(
    (nextValue: string): void => {
      const sanitized = sanitizeRichTextHtml(nextValue)
      if (!isControlled) {
        setInternalValue(sanitized)
      }

      onChange?.(sanitized, richTextEditorHtmlToPlainText(sanitized))
    },
    [isControlled, onChange],
  )

  const refreshActiveCommands = useCallback((): void => {
    if (disabled) {
      return
    }

    const nextActive = new Set<RichTextEditorToolbarItemId>()

    FORMATTING_COMMANDS.forEach((command) => {
      if (isCommandActive(command)) {
        nextActive.add(command)
      }
    })

    setActiveCommands(nextActive)
    setAlignment(detectAlignment())
  }, [disabled])

  const focusEditor = useCallback((): void => {
    editorRef.current?.focus()
  }, [])

  const handleInput = useCallback((): void => {
    syncValue(getEditorHtml(editorRef.current))
    refreshActiveCommands()
  }, [refreshActiveCommands, syncValue])

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>): void => {
      if (disabled) {
        return
      }

      event.preventDefault()
      const html = event.clipboardData.getData('text/html')
      const text = event.clipboardData.getData('text/plain')

      if (html) {
        document.execCommand('insertHTML', false, sanitizeRichTextHtml(html))
      } else if (text) {
        document.execCommand('insertText', false, text)
      }

      handleInput()
    },
    [disabled, handleInput],
  )

  const applyAlignment = useCallback(
    (nextAlignment: TextAlignment): void => {
      focusEditor()
      document.execCommand(ALIGNMENT_COMMAND_MAP[nextAlignment], false)
      setAlignment(nextAlignment)
      handleInput()
    },
    [focusEditor, handleInput],
  )

  const applyFontSize = useCallback(
    (fontSize: RichTextEditorFontSize): void => {
      focusEditor()
      document.execCommand('fontSize', false, FONT_SIZE_COMMAND_MAP[fontSize])
      handleInput()
    },
    [focusEditor, handleInput],
  )

  const applyTextColor = useCallback(
    (color: string): void => {
      focusEditor()
      document.execCommand('foreColor', false, color)
      handleInput()
    },
    [focusEditor, handleInput],
  )

  const applyBackgroundColor = useCallback(
    (color: string): void => {
      focusEditor()
      document.execCommand('hiliteColor', false, color)
      handleInput()
    },
    [focusEditor, handleInput],
  )

  const insertText = useCallback(
    (text: string): void => {
      const editor = editorRef.current

      if (!editor) {
        return
      }

      editor.focus()

      const selection = document.getSelection()

      // A click outside the editor can leave the caret elsewhere; fall back to
      // appending at the end so the text never lands outside the editor.
      if (
        !selection ||
        selection.rangeCount === 0 ||
        !editor.contains(selection.anchorNode)
      ) {
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        selection?.removeAllRanges()
        selection?.addRange(range)
      }

      document.execCommand('insertText', false, text)
      handleInput()
    },
    [handleInput],
  )

  const insertVariable = useCallback(
    (variable: RichTextEditorVariable): void => {
      const editor = editorRef.current

      if (!editor || disabled) {
        return
      }

      editor.focus()

      const selection = document.getSelection()

      if (
        !selection ||
        selection.rangeCount === 0 ||
        !editor.contains(selection.anchorNode)
      ) {
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        selection?.removeAllRanges()
        selection?.addRange(range)
      }

      const token = document.createElement('span')
      token.className = RICH_TEXT_EDITOR_VARIABLE_CLASS
      token.setAttribute('data-variable-id', variable.id)
      token.textContent = variable.value ?? variable.label

      // The trailing space sits outside the token so typing after a variable
      // continues as plain (non-blue) text.
      document.execCommand(
        'insertHTML',
        false,
        `${sanitizeRichTextHtml(token.outerHTML)}&nbsp;`,
      )
      handleInput()
    },
    [disabled, handleInput],
  )

  const runToolbarAction = useCallback(
    (actionId: RichTextEditorToolbarItemId): boolean => {
      if (disabled || actionId === 'divider') {
        return false
      }

      focusEditor()

      if (
        actionId === 'alignment' ||
        actionId === 'fontSize' ||
        actionId === 'textColor' ||
        actionId === 'bgColor'
      ) {
        return false
      }

      if (actionId === 'highlight') {
        document.execCommand('hiliteColor', false, '#FFF3B0')
        handleInput()
        return true
      }

      if (actionId === 'link') {
        const url = window.prompt('Enter link URL')

        if (!url) {
          return false
        }

        document.execCommand('createLink', false, url)
        handleInput()
        return true
      }

      const command = EXEC_COMMAND_MAP[actionId]

      if (!command) {
        return false
      }

      if (actionId === 'blockquote') {
        document.execCommand(command, false, 'blockquote')
      } else {
        document.execCommand(command, false)
      }

      handleInput()
      return true
    },
    [disabled, focusEditor, handleInput],
  )

  useEffect(() => {
    const editor = editorRef.current

    if (!editor) {
      return
    }

    if (isControlled) {
      const nextHtml = sanitizeRichTextHtml(value ?? '')
      if (editor.innerHTML !== nextHtml) {
        editor.innerHTML = nextHtml
      }

      return
    }

    if (!editor.innerHTML && defaultValue) {
      editor.innerHTML = sanitizeRichTextHtml(defaultValue)
    }
  }, [defaultValue, isControlled, value])

  useEffect(() => {
    if (disabled) {
      return undefined
    }

    const handleSelectionChange = (): void => {
      const editor = editorRef.current
      const selection = document.getSelection()

      if (!editor || !selection || selection.rangeCount === 0) {
        return
      }

      if (!editor.contains(selection.anchorNode)) {
        return
      }

      refreshActiveCommands()
    }

    document.addEventListener('selectionchange', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [disabled, refreshActiveCommands])

  return {
    editorRef,
    resolvedValue,
    activeCommands,
    alignment,
    setAlignment,
    applyAlignment,
    applyFontSize,
    applyTextColor,
    applyBackgroundColor,
    insertText,
    insertVariable,
    runToolbarAction,
    handleInput,
    handlePaste,
    refreshActiveCommands,
  }
}

export const ALIGNMENT_OPTIONS: TextAlignment[] = [
  'left',
  'center',
  'right',
  'justify',
]

export const ALIGNMENT_ICONS: Record<
  TextAlignment,
  FunctionComponent<SVGProps<SVGSVGElement>>
> = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
  justify: AlignJustifyIcon,
}
